import type { Fraction } from '../../util/fractionType';
import { frac, multiplyFractions } from '../../util/integerMath';
import type { FuzzyValue } from '../../util/fuzzyNumbers';
import { fractionToFuzzy, multiplyFuzzy } from '../../util/fuzzyMath';
import { invariant } from '../../util/invariant';
import type { ResourceLocation } from '../../io/resource';
import type { ClipRewrapInfo } from '../clipRewrapInfo';

/**
 *  Provides all the information that is required from an item in order to use
 *  it in an MXF rewrap operation
 */
export interface MxfRewrapItem {
  id: string,
  highRes: ResourceLocation,
  index: ResourceLocation,
  frameRate: FuzzyValue,
  segmentDuration: Fraction | null,
  compatibilityHash: string | null,
  origin: number,
}

export interface MxfRewrapItemProvider {
  getRewrapItems(itemIds: string[]): Promise<MxfRewrapItem[]>;
}

export interface MxfRewrapInput {
  highRes: ResourceLocation
  index: ResourceLocation,
  inPoint: number,
  outPoint: number,
  compatibilityHash: string | null,
  origin: number,
}

export async function tsSegmentRangeToMxfRewrapInput(
  services: {
    mxfRewrapItemProvider: MxfRewrapItemProvider,
  },
  itemId: string,
  startSegmentTime: number,
  endSegmentTime: number,
): Promise<MxfRewrapInput> {
  const { mxfRewrapItemProvider } = services;
  const [item] = await mxfRewrapItemProvider.getRewrapItems([itemId]);

  if (item.segmentDuration === null) {
    throw Error('Expected item to have segmentDuration');
  }

  const segmentIn = frac(startSegmentTime, 1);
  const segmentOut = frac(endSegmentTime, 1);

  const inPointSeconds = multiplyFractions(segmentIn, item.segmentDuration);
  const outPointSeconds = multiplyFractions(segmentOut, item.segmentDuration);

  const inPoint = Math.round(multiplyFuzzy(fractionToFuzzy(inPointSeconds), item.frameRate).approximate);
  const outPoint = Math.round(multiplyFuzzy(fractionToFuzzy(outPointSeconds), item.frameRate).approximate);


  return {
    highRes: item.highRes,
    index: item.index,
    inPoint,
    outPoint,
    compatibilityHash: item.compatibilityHash,
    origin: item.origin,
  };
}

export async function clipListToMxfRewrapInputs(
  services: {
    mxfRewrapItemProvider: MxfRewrapItemProvider,
  },
  clips: ClipRewrapInfo[],
): Promise<MxfRewrapInput[]> {
  const { mxfRewrapItemProvider } = services;
  const inputs : MxfRewrapInput[] = [];
  const uniqueItemIds = [...(new Set(clips.map((clip) => clip.itemId)))];
  const items = await mxfRewrapItemProvider.getRewrapItems(uniqueItemIds);
  for (const clip of clips) {
    const item = items.find((i) => i.id === clip.itemId);
    invariant(item !== undefined, 'expected item');
    const inPoint = Math.round(multiplyFuzzy(fractionToFuzzy(clip.inPointInSeconds), item.frameRate).approximate);
    const outPoint = Math.round(multiplyFuzzy(fractionToFuzzy(clip.outPointInSeconds), item.frameRate).approximate);
    inputs.push({
      inPoint,
      outPoint,
      highRes: item.highRes,
      index: item.index,
      compatibilityHash: item.compatibilityHash,
      origin: item.origin,
    });
  }
  return inputs;
}

